// Curated city × material combinations for SEO landing pages.
// Each entry powers a /gravel-delivery/:citySlug/:materialSlug route via
// pages/CityMaterial.tsx. The list mixes King and Pierce County cities so
// the index page shows our full service footprint at a glance, not just
// the southern corridor.
import { categories } from './products'
import { getZoneForPosition } from './deliveryZones'
export type ServiceCity = {
  slug: string
  name: string
  state: string
  county: 'King' | 'Pierce'
  zone: number
  // Approximate city centroid [lat, lng] — used by the drift-detection
  // check below to make sure the curated `zone` value matches what the
  // delivery-zone polygons would actually return for this city. If the
  // polygons get redrawn, the check logs a warning and we can update the
  // `zone` value here in one place.
  coords: [number, number]
  // Display address used to prefill the estimator and seed the
  // Nominatim geocode on the destination page.
  geocodeHint: string
  neighborhoods: string[]
  // Common short trip notes — used in the page intro paragraph.
  blurb: string
}
export const SERVICE_CITIES: ServiceCity[] = [
  {
    slug: 'tacoma',
    name: 'Tacoma',
    state: 'WA',
    county: 'Pierce',
    zone: 3,
    coords: [47.2529, -122.4443],
    geocodeHint: 'Tacoma, WA',
    neighborhoods: [
      'North End',
      'Stadium District',
      'Hilltop',
      'South Tacoma',
      'Browns Point',
      'University Place',
    ],
    blurb:
      'We deliver into Tacoma daily — most loads route directly down I-5 with reliable next-day scheduling.',
  },
  {
    slug: 'seattle',
    name: 'Seattle',
    state: 'WA',
    county: 'King',
    zone: 3,
    coords: [47.6062, -122.3321],
    geocodeHint: 'Seattle, WA',
    neighborhoods: [
      'West Seattle',
      'Beacon Hill',
      'Rainier Valley',
      'Georgetown',
      'Magnolia',
      'Queen Anne',
    ],
    blurb:
      'We deliver into Seattle daily. South-end neighborhoods route fastest; north of the ship canal adds an hour to dispatch.',
  },
  {
    slug: 'puyallup',
    name: 'Puyallup',
    state: 'WA',
    county: 'Pierce',
    zone: 4,
    coords: [47.1854, -122.2929],
    geocodeHint: 'Puyallup, WA',
    neighborhoods: [
      'South Hill',
      'Riverside',
      'North Puyallup',
      'Edgerton',
      'Glacier View',
    ],
    blurb:
      'Puyallup sits at our outer Pierce reach — book a day ahead so we can line up the right truck and timing.',
  },
  {
    slug: 'bellevue',
    name: 'Bellevue',
    state: 'WA',
    county: 'King',
    zone: 3,
    coords: [47.6101, -122.2015],
    geocodeHint: 'Bellevue, WA',
    neighborhoods: [
      'Downtown',
      'Bridle Trails',
      'Somerset',
      'Newport',
      'Lake Hills',
      'Wilburton',
    ],
    blurb:
      'Bellevue routes off I-405 — solid lead times for both Eastside residential and commercial sites.',
  },
  {
    slug: 'mercer-island',
    name: 'Mercer Island',
    state: 'WA',
    county: 'King',
    zone: 3,
    coords: [47.5707, -122.2221],
    geocodeHint: 'Mercer Island, WA',
    neighborhoods: [
      'North End',
      'East Seattle',
      'Mercerwood',
      'First Hill',
      'Island Crest',
    ],
    blurb:
      'Mercer Island runs off I-90 in both directions. Many island driveways are narrow and tree-lined, so tell us the approach and we’ll send the right truck.',
  },
  {
    slug: 'medina',
    name: 'Medina',
    state: 'WA',
    county: 'King',
    zone: 3,
    coords: [47.6207, -122.2276],
    geocodeHint: 'Medina, WA',
    neighborhoods: [
      'Medina Heights',
      'Evergreen Point',
      'Groat Point',
      'Overlake Golf',
      'Hunts Point',
    ],
    blurb:
      'Medina deliveries route through Bellevue off 520. Tight lakeside lots and mature landscaping are the norm here, so we plan the drop spot before the truck rolls.',
  },
  {
    slug: 'kent',
    name: 'Kent',
    state: 'WA',
    county: 'King',
    zone: 1,
    coords: [47.3809, -122.2348],
    geocodeHint: 'Kent, WA',
    neighborhoods: [
      'East Hill',
      'West Hill',
      'Panther Lake',
      'Meridian',
      'Lake Meridian',
    ],
    blurb:
      'Kent is in our innermost delivery zone — fastest routes and consistent same-day availability.',
  },
  {
    slug: 'renton',
    name: 'Renton',
    state: 'WA',
    county: 'King',
    zone: 1,
    coords: [47.4829, -122.2171],
    geocodeHint: 'Renton, WA',
    neighborhoods: [
      'Highlands',
      'Kennydale',
      'Talbot Hill',
      'The Landing',
      'Fairwood',
    ],
    blurb:
      'Renton sits in our innermost delivery zone — most loads roll within an hour of confirmation during business hours.',
  },
  {
    slug: 'auburn',
    name: 'Auburn',
    state: 'WA',
    county: 'King',
    zone: 2,
    coords: [47.3073, -122.2285],
    geocodeHint: 'Auburn, WA',
    neighborhoods: [
      'Lea Hill',
      'West Hill',
      'Lakeland Hills',
      'Downtown Auburn',
    ],
    blurb:
      'Auburn deliveries route directly off 167 — quick turnarounds for both residential and contractor jobsites.',
  },
  {
    slug: 'federal-way',
    name: 'Federal Way',
    state: 'WA',
    county: 'King',
    zone: 2,
    coords: [47.3223, -122.3126],
    geocodeHint: 'Federal Way, WA',
    neighborhoods: [
      'Twin Lakes',
      'Mirror Lake',
      'Lakota',
      'Redondo',
      'Brigadoon',
    ],
    blurb:
      'Federal Way sits in our core delivery zone with reliable same-week scheduling.',
  },
  {
    slug: 'burien',
    name: 'Burien',
    state: 'WA',
    county: 'King',
    zone: 2,
    coords: [47.4704, -122.3468],
    geocodeHint: 'Burien, WA',
    neighborhoods: [
      'Three Tree Point',
      'Gregory Heights',
      'Boulevard Park',
      'Seahurst',
      'Shorewood',
    ],
    blurb:
      'Burien is a short run from our South Sound routes, so same-day slots open up often.',
  },
  {
    slug: 'snoqualmie',
    name: 'Snoqualmie',
    state: 'WA',
    county: 'King',
    zone: 4,
    coords: [47.5287, -121.8254],
    geocodeHint: 'Snoqualmie, WA',
    neighborhoods: [
      'Snoqualmie Ridge',
      'Eagle Lake',
      'Kimball Creek',
      'Downtown Snoqualmie',
      'Echo Glen',
    ],
    blurb:
      'Snoqualmie is one of the fastest-growing corners of King County — new construction on the Ridge means driveway base, drain rock, and landscape soil in volume.',
  },
  {
    slug: 'kirkland',
    name: 'Kirkland',
    state: 'WA',
    county: 'King',
    zone: 4,
    coords: [47.6769, -122.2059],
    geocodeHint: 'Kirkland, WA',
    neighborhoods: [
      'Juanita',
      'Totem Lake',
      'Rose Hill',
      'Houghton',
      'Bridle Trails',
    ],
    blurb:
      'Kirkland sits at our northern Eastside reach — book a day ahead and we can usually hit a morning window.',
  },
  {
    slug: 'redmond',
    name: 'Redmond',
    state: 'WA',
    county: 'King',
    zone: 4,
    coords: [47.674, -122.1215],
    geocodeHint: 'Redmond, WA',
    neighborhoods: [
      'Education Hill',
      'Grass Lawn',
      'Idylwood',
      'Overlake',
      'Bear Creek',
    ],
    blurb:
      'Redmond deliveries pair well with weekday morning slots — book ahead during peak landscape season.',
  },
  {
    slug: 'sammamish',
    name: 'Sammamish',
    state: 'WA',
    county: 'King',
    zone: 3,
    coords: [47.6163, -122.0356],
    geocodeHint: 'Sammamish, WA',
    neighborhoods: [
      'Klahanie',
      'Pine Lake',
      'Trossachs',
      'Beaver Lake',
      'Inglewood',
    ],
    blurb:
      'Sammamish sits at our outer Eastside reach — schedule a day or two ahead for best timing.',
  },
  {
    slug: 'gig-harbor',
    name: 'Gig Harbor',
    state: 'WA',
    county: 'Pierce',
    zone: 4,
    coords: [47.3293, -122.5826],
    geocodeHint: 'Gig Harbor, WA',
    neighborhoods: [
      'Downtown',
      'Canterwood',
      'Rosedale',
      'Crescent Valley',
      'Fox Island',
      'Artondale',
    ],
    blurb:
      'Gig Harbor deliveries route over the Narrows Bridge — book a day ahead and we’ll line up the timing around bridge traffic.',
  },
  {
    slug: 'black-diamond',
    name: 'Black Diamond',
    state: 'WA',
    county: 'King',
    zone: 1,
    coords: [47.3087, -122.0046],
    geocodeHint: 'Black Diamond, WA',
    neighborhoods: [
      'Ten Trails',
      'Lake Sawyer',
      'Pipe Lake',
      'Diamond Lake',
      'Old Town',
    ],
    blurb:
      'Black Diamond sits in our inner delivery zone with quick turnarounds — popular for rural driveway builds and acreage projects.',
  },
  {
    slug: 'bonney-lake',
    name: 'Bonney Lake',
    state: 'WA',
    county: 'Pierce',
    zone: 3,
    coords: [47.1865, -122.1865],
    geocodeHint: 'Bonney Lake, WA',
    neighborhoods: [
      'Tehaleh',
      'Sky Island',
      'Lake Tapps',
      'Falling Water',
      'Eastown',
    ],
    blurb:
      'Bonney Lake routes off the Puyallup corridor — schedule a day ahead so we can route the truck efficiently.',
  },
  {
    slug: 'maple-valley',
    name: 'Maple Valley',
    state: 'WA',
    county: 'King',
    zone: 1,
    coords: [47.3923, -122.0454],
    geocodeHint: 'Maple Valley, WA',
    neighborhoods: [
      'Four Corners',
      'Lake Wilderness',
      'Cedar Downs',
      'Elk Run',
      'Summit Place',
    ],
    blurb:
      'Maple Valley runs straight out the Kent-Kangley corridor from the yard — inner-zone pricing and consistent same-day availability.',
  },
  {
    slug: 'covington',
    name: 'Covington',
    state: 'WA',
    county: 'King',
    zone: 1,
    coords: [47.3592, -122.1215],
    geocodeHint: 'Covington, WA',
    neighborhoods: [
      'Timberlane',
      'Jenkins Creek',
      'Meridian Valley',
      'Lake Sawyer Road',
      'Pipe Lake',
    ],
    blurb:
      'Covington sits in our innermost ring off 272nd — short haul, quick turnarounds on both residential and contractor loads.',
  },
  {
    slug: 'tukwila',
    name: 'Tukwila',
    state: 'WA',
    county: 'King',
    zone: 1,
    coords: [47.4738, -122.2606],
    geocodeHint: 'Tukwila, WA',
    neighborhoods: [
      'Allentown',
      'Riverton Heights',
      'Foster',
      'Southcenter',
      'Thorndyke',
    ],
    blurb:
      'Tukwila is minutes from the yard off 405 and 599 — one of the fastest routes we run, with same-day on most morning orders.',
  },
  {
    slug: 'newcastle',
    name: 'Newcastle',
    state: 'WA',
    county: 'King',
    zone: 1,
    coords: [47.5301, -122.1554],
    geocodeHint: 'Newcastle, WA',
    neighborhoods: [
      'China Creek',
      'Olympus',
      'Hazelwood',
      'Newport Hills',
      'Lake Boren',
    ],
    blurb:
      'Newcastle routes off Coal Creek Parkway. Hillside driveways are common here, so tell us the grade and approach and we will match the truck to it.',
  },
  {
    slug: 'seatac',
    name: 'SeaTac',
    state: 'WA',
    county: 'King',
    zone: 2,
    coords: [47.4436, -122.2963],
    geocodeHint: 'SeaTac, WA',
    neighborhoods: [
      'Angle Lake',
      'McMicken Heights',
      'Bow Lake',
      'Riverton',
      'Madrona Park',
    ],
    blurb:
      'SeaTac deliveries run off 518 and International Boulevard — straightforward routing, with airport-area jobsites handled daily.',
  },
  {
    slug: 'des-moines',
    name: 'Des Moines',
    state: 'WA',
    county: 'King',
    zone: 2,
    coords: [47.4018, -122.3243],
    geocodeHint: 'Des Moines, WA',
    neighborhoods: [
      'Woodmont',
      'Zenith',
      'Redondo',
      'North Hill',
      'Marina District',
    ],
    blurb:
      'Des Moines routes down Marine View Drive. Waterfront lots often mean tight approaches — send a photo of the drop spot and we will plan the pour.',
  },
  {
    slug: 'issaquah',
    name: 'Issaquah',
    state: 'WA',
    county: 'King',
    zone: 2,
    coords: [47.5301, -122.0326],
    geocodeHint: 'Issaquah, WA',
    neighborhoods: [
      'Issaquah Highlands',
      'Squak Mountain',
      'Talus',
      'Olde Town',
      'Sammamish Plateau',
    ],
    blurb:
      'Issaquah runs off I-90 in both directions. Plateau and hillside addresses add drive time, so a day of notice gets you a better window.',
  },
  {
    slug: 'enumclaw',
    name: 'Enumclaw',
    state: 'WA',
    county: 'King',
    zone: 3,
    coords: [47.2043, -121.9915],
    geocodeHint: 'Enumclaw, WA',
    neighborhoods: [
      'Downtown Enumclaw',
      'Krain',
      'Osceola',
      'Cumberland',
      'Buckley Road',
    ],
    blurb:
      'Enumclaw is plateau country — long driveways, pasture access, and real mud in winter. We haul out here regularly; book a day ahead for the best window.',
  },
  {
    slug: 'sumner',
    name: 'Sumner',
    state: 'WA',
    county: 'Pierce',
    zone: 2,
    coords: [47.2032, -122.2404],
    geocodeHint: 'Sumner, WA',
    neighborhoods: [
      'Downtown Sumner',
      'Elhi Hill',
      'Rivergrove',
      'Sumner Heights',
      'Orting Highway',
    ],
    blurb:
      'Sumner sits right on the 167 corridor — an easy run from the yard and one of our steadier Pierce County routes.',
  },
  {
    slug: 'lakewood',
    name: 'Lakewood',
    state: 'WA',
    county: 'Pierce',
    zone: 4,
    coords: [47.1718, -122.5185],
    geocodeHint: 'Lakewood, WA',
    neighborhoods: [
      'Lakewood Colonial Center',
      'Tillicum',
      'American Lake',
      'Oakbrook',
      'Steilacoom Lake',
    ],
    blurb:
      'Lakewood is our outer Pierce reach off I-5. Schedule ahead and we will batch the run — the haul is longer, so full loads price best here.',
  },
]
// Featured products to generate combo pages for. Only top sellers — long-tail
// products still get organic SEO via their /shop/:slug product pages.
export const SERVICE_PRODUCT_SLUGS = [
  '5-8-minus', // 5/8" Minus — driveway surface
  '1-25-minus', // 1.25" Minus — driveway base
  '5-8-clear', // 5/8" Crushed Clear — clean drainage rock
  '7-8-drain-rock', // 7/8" Drain Rock
  '3-way-topsoil',
  'medium-bark',
]
// ---------- Per-material editorial content ----------
//
// The city × material pages used to lean entirely on the catalog blurb, which
// left them thinner than the supplier pages they compete with on queries like
// "5/8 minus Seattle". Each curated material now carries a short explainer,
// concrete depth guidance, the compaction-vs-drainage distinction people get
// wrong, and its own FAQ set. City name is interpolated so the copy reads
// local without duplicating a paragraph per city.
export interface MaterialGuide {
  /** 100–150 word plain-language explainer. */
  what: string
  /** Short, scannable use cases. */
  uses: string[]
  /** Why this material suits Western Washington, with the city named. */
  localAngle: (city: string) => string
  /** How deep to place it, by job. */
  depths: { job: string; depth: string }[]
  /** The mistake customers make — compaction vs drainage, mulch vs soil. */
  caution: { title: string; body: string }
  faqs: (city: string, pricePerYard: number) => { q: string; a: string }[]
}
export const MATERIAL_GUIDES: Record<string, MaterialGuide> = {
  '5-8-minus': {
    what: 'Crushed quarry rock screened to 5/8 inch with the fines — the rock dust produced during crushing — left in the mix. Those fines are the whole point: as the angular fragments are compacted, the dust fills the voids between them and the layer locks into a firm, dense surface that holds a grade and sheds water instead of rutting. County plans and contractors call it CSTC, crushed surfacing top course. Around Puget Sound it is the default finish layer for driveways, parking pads, and pathways, and the standard base under pavers and shed floors. Because it compacts tight rather than draining, it is the opposite of a washed drain rock — pick it when you want a surface you can drive and walk on.',
    uses: [
      'Driveway surfacing',
      'Parking pads and turnarounds',
      'Pathways and walkways',
      'Pothole and rut repair',
      'Shed, shop, and RV pads',
      'Compacted base under pavers',
    ],
    localAngle: (city) =>
      `Western Washington driveways fail from water sitting in a soft base, not from the surface wearing out. Because 5/8" Minus compacts into a dense layer and is crowned to shed rain to the shoulders, it holds up through a ${city} winter far better than a clean rock that lets water travel straight down into the subgrade. On ground that already pumps or ruts, build 4 inches of 1.25" Minus first, compact it, then finish with 5/8" Minus on top.`,
    depths: [
      { job: 'Refresh an existing driveway', depth: '2–3" compacted' },
      { job: 'Full driveway rebuild', depth: '4" of 1.25" Minus base + 2–3" surface' },
      { job: 'Walkway or garden path', depth: '2" compacted' },
      { job: 'Shed, shop, or RV pad', depth: '4" compacted in two lifts' },
      { job: 'Base under pavers', depth: '4" compacted, then 1" sand' },
    ],
    caution: {
      title: 'This is not a drainage rock',
      body: 'The fines that make 5/8" Minus compact are exactly what stops water from moving through it. If the job is a French drain, a footing drain, or a dry well, use a washed clean rock such as 7/8" Drain Rock instead. Minus for surfaces you drive on, clean for water you need to move.',
    },
    faqs: (city, price) => [
      {
        q: `Is 5/8" minus good for driveways?`,
        a: `Yes — it is the standard driveway surface in the ${city} area. The angular rock and fines compact into a hard layer that stays put under vehicle traffic, and it can be regraded and topped off years later rather than replaced. Compact it in lifts with a plate compactor and crown the centre slightly so rain runs to the shoulders.`,
      },
      {
        q: `Does 5/8" minus drain well?`,
        a: `No, and that is by design. The fines fill the voids so the layer compacts tight, which means water runs across the surface rather than through it. That is what you want on a driveway. If you need water to move down through the rock, order a washed product like 7/8" Drain Rock.`,
      },
      {
        q: `How deep should 5/8" minus be for a driveway?`,
        a: `Two to three inches compacted is enough to resurface a driveway that still has a solid base. If the ground underneath is soft, rutted, or pumping mud, put down 4 inches of 1.25" Minus first, compact it, then finish with 2–3 inches of 5/8" Minus.`,
      },
      {
        q: `How many yards of 5/8" minus do I need?`,
        a: `One cubic yard covers roughly 100 sq ft at 3 inches deep. Use the calculator on this page for your exact dimensions — it rounds up to a deliverable amount. At ${price} per yard plus the ${city} haul, a typical two-car driveway refresh lands in the 5–8 yard range.`,
      },
    ],
  },
  '1-25-minus': {
    what: 'Crushed quarry rock screened to 1.25 inch with the fines left in — the coarser cousin of 5/8" Minus, and the layer that goes underneath it. The larger angular pieces bridge and carry load; the fines lock the whole layer together as it is compacted. Plans and county specs call it CSBC, crushed surfacing base course. This is base material, not a finish surface: it is too coarse to walk on comfortably and too open to grade smooth, which is why it is almost always capped with 2–3 inches of 5/8" Minus. Reach for it when the ground underneath is soft, rutted, or pumping mud, or when the driveway will carry trucks, RVs, or equipment rather than just cars.',
    uses: [
      'Driveway base course',
      'Soft, rutted, or muddy ground',
      'Road and access-road base',
      'Heavy parking and equipment pads',
      'Structural fill under concrete',
      'Deep pothole and washout repair',
    ],
    localAngle: (city) =>
      `Most failed driveways around ${city} are base failures, not surface failures — the top rock is fine, but there is nothing structural under it, so it ruts every winter. Four inches of 1.25" Minus compacted in two lifts gives the driveway something to sit on. Push a length of rebar into your existing driveway by hand: if it slides in past four or five inches, you need base, not another load of surfacing.`,
    depths: [
      { job: 'Driveway base course', depth: '4" compacted in two lifts' },
      { job: 'Over soft or muddy ground', depth: '6–8", fabric underneath' },
      { job: 'Heavy / equipment pad', depth: '6" compacted' },
      { job: 'Under a concrete slab', depth: '4" compacted' },
      { job: 'Deep pothole repair', depth: 'Fill to 3" below grade, then surface' },
    ],
    caution: {
      title: 'It is a base, not a finish surface',
      body: 'Left exposed, 1.25" Minus stays coarse and loose on top and never grades out smooth. Always cap it with 2–3 inches of 5/8" Minus. And on genuinely saturated ground, lay geotextile fabric first — without it the rock migrates down into the mud and you lose the layer within a season.',
    },
    faqs: (city, price) => [
      {
        q: `What is the difference between 1.25" minus and 5/8" minus?`,
        a: `Size and job. 1.25" Minus is the coarse structural base that carries the load; 5/8" Minus is the finer surface you drive and walk on. A proper driveway uses both — roughly 4 inches of 1.25" underneath, 2–3 inches of 5/8" on top. Using only the surface rock on soft ground is the most common reason a ${city} driveway ruts.`,
      },
      {
        q: `Do I need fabric under 1.25" minus?`,
        a: `On firm ground, no. On soft, wet, or pumping subgrade, yes — geotextile fabric keeps the rock and the mud from mixing, which is what makes a base disappear. Fabric is cheap compared with the rock it saves.`,
      },
      {
        q: `Can I use 1.25" minus as my driveway surface?`,
        a: `You can drive on it, but you will not like it. The larger pieces stay proud of the surface, it is rough underfoot, and it never grades smooth. Cap it with 5/8" Minus and you get the structure of one and the finish of the other.`,
      },
      {
        q: `How much 1.25" minus do I need?`,
        a: `One yard covers about 80 sq ft at 4 inches deep. Run your driveway dimensions through the calculator on this page for an exact figure, and add roughly 15% for compaction. Material is ${price} per yard plus the ${city} haul.`,
      },
    ],
  },
  '5-8-clear': {
    what: 'Crushed rock screened to 5/8 inch and then washed, so every fine is rinsed out. What is left is angular, uniform, and open: the stones interlock enough to stay where you put them, but the voids between them stay clear, so water passes straight through. That combination makes it the tidy, general-purpose clean rock — drainage work where a smaller stone is easier to place, pipe bedding, gravel around foundations and side yards, and decorative ground cover that does not track mud into the house. Because it is washed rather than dusty, it also looks clean the day it is dumped and stays that way, which is why it turns up in landscape designs as often as in drainage trenches.',
    uses: [
      'Drainage trenches and swales',
      'Pipe bedding',
      'Foundation and side-yard gravel',
      'Dry creek beds',
      'Clean decorative ground cover',
      'Dog runs and utility areas',
    ],
    localAngle: (city) =>
      `Side yards and foundation strips are where ${city} houses get into trouble — narrow, shaded, and wet from October through May. A clean 5/8" rock over fabric in those runs keeps water moving away from the wall instead of standing against it, and unlike a minus rock it never turns to mud underfoot. It is also the easier clean rock to shovel and rake by hand where a wheelbarrow is your only access.`,
    depths: [
      { job: 'Drainage trench', depth: '8–12" around the pipe' },
      { job: 'Pipe bedding', depth: '4" under, 6" over' },
      { job: 'Side-yard / foundation strip', depth: '3–4" over fabric' },
      { job: 'Decorative ground cover', depth: '2–3" over fabric' },
      { job: 'Dry creek bed', depth: '4", larger rock at the edges' },
    ],
    caution: {
      title: 'Clean rock will not compact',
      body: 'With no fines there is nothing to bind the stones, so 5/8" Crushed Clear stays loose by design. Do not use it as a driveway surface or under pavers — it shifts and migrates. For anything that carries weight, use 5/8" Minus. And lay fabric underneath in landscape use, or soil works up into the rock within a couple of seasons.',
    },
    faqs: (city, price) => [
      {
        q: `What is the difference between 5/8" clean and 5/8" minus?`,
        a: `The fines. 5/8" Minus keeps the rock dust from crushing, so it compacts into a hard surface. 5/8" Crushed Clear is washed, so the voids stay open and water drains through. Same size stone, opposite jobs — one carries weight, the other moves water.`,
      },
      {
        q: `Should I use 5/8" clean or 7/8" drain rock?`,
        a: `Both are washed and both drain. Use 7/8" Drain Rock for French drains, footing drains, and dry wells, where the larger voids move more water. Use 5/8" Crushed Clear where the stone is being shovelled by hand, placed in a narrow run, or left visible as ground cover.`,
      },
      {
        q: `Can I use clean rock for a driveway?`,
        a: `No. Without fines it never locks together, so it ruts under tires and migrates onto the lawn. Order 5/8" Minus for driveways and pads, and keep the clean rock for drainage and landscape use.`,
      },
      {
        q: `How much 5/8" clean rock do I need?`,
        a: `One yard covers roughly 100 sq ft at 3 inches deep. Use the calculator on this page for your exact area or trench, and it will round to a deliverable load. Material runs ${price} per yard plus the ${city} delivery rate.`,
      },
    ],
  },
  '7-8-drain-rock': {
    what: 'Washed, screened rock at roughly 7/8 inch with every last fine rinsed out. With no dust in the mix the stones stack loosely and the voids between them stay open, so water moves through the layer quickly instead of pooling. That makes it the working material for French drains, footing and perimeter drains, dry wells, and retaining-wall backfill, and it is the rock most drainage details on a set of plans are asking for. It never compacts into a solid surface — that is the trade-off you are buying. Wrap the trench in filter fabric so surrounding soil cannot migrate in and silt up the voids over time.',
    uses: [
      'French drains',
      'Footing and perimeter drains',
      'Dry wells and infiltration pits',
      'Retaining wall backfill',
      'Pipe bedding',
      'Splash strips along foundations',
    ],
    localAngle: (city) =>
      `Puget Sound rain is persistent rather than dramatic, so ${city} drainage problems are usually about water that never gets a route away from the house. Drain rock gives it that route: open voids in the trench let water fall through, reach the perforated pipe, and leave. Line the trench with filter fabric before you place the rock — silt is what kills a French drain, not the rain.`,
    depths: [
      { job: 'French drain trench', depth: '12" of rock around a 4" perforated pipe' },
      { job: 'Footing / perimeter drain', depth: '6–12" over the pipe, fabric-wrapped' },
      { job: 'Dry well', depth: 'Fill the pit, leave 6" of soil cover' },
      { job: 'Retaining wall backfill', depth: '12" wide behind the full wall height' },
      { job: 'Foundation splash strip', depth: '3–4" over fabric' },
    ],
    caution: {
      title: 'Do not use it as a driving surface',
      body: 'With no fines, clean rock never locks together — it shifts underfoot and under tires and migrates out of the area. For a driveway, pad, or path, order 5/8" Minus instead. Clean rock moves water; minus rock carries weight.',
    },
    faqs: (city, price) => [
      {
        q: `What is 7/8" drain rock used for?`,
        a: `French drains, footing and perimeter drains, dry wells, pipe bedding, and backfill behind retaining walls — anywhere water needs a fast path away from a structure. It is washed clean of fines, so the voids between the stones stay open and water keeps moving.`,
      },
      {
        q: `Can I use drain rock on a driveway?`,
        a: `No. Without fines it never compacts, so it shifts under tires, migrates off the edges, and rides up into the lawn. Use 5/8" Minus for driveways and pads and save the drain rock for drainage.`,
      },
      {
        q: `How deep should a French drain be?`,
        a: `A typical residential French drain runs 18–24 inches deep with the trench lined in filter fabric, a few inches of drain rock, a 4-inch perforated pipe, then rock over the pipe and the fabric folded closed on top. Slope the trench about 1 inch per 10 feet toward the outlet.`,
      },
      {
        q: `How much drain rock do I need in ${city}?`,
        a: `Run your trench length, width, and depth through the calculator on this page — it converts to cubic yards and rounds to a deliverable load. As a guide, 100 feet of 12-inch-wide trench at 12 inches deep takes about 4 yards. Material runs ${price} per yard plus the ${city} haul.`,
      },
    ],
  },
  '3-way-topsoil': {
    what: 'A blended garden soil — screened topsoil, sand, and compost mixed three ways, which is where the name comes from. The compost carries the organic matter and nutrients, the sand keeps the blend from setting up like clay, and the screened soil gives it body. Screening removes rock, root, and debris so it rakes out evenly and seed makes good contact. It is the regional default for new lawns, garden beds, raised beds, and levelling low spots in a yard. It is a growing medium, not a structural fill: expect it to settle, and never use it as a base under anything you intend to build on or drive over.',
    uses: [
      'New lawn installation',
      'Garden and flower beds',
      'Raised bed fill',
      'Levelling low spots in a yard',
      'Topdressing before overseeding',
      'Regrading around a house',
    ],
    localAngle: (city) =>
      `A lot of ${city} yards are sitting on compacted glacial till or leftover construction fill, which drains badly and grows grass reluctantly. Four inches of 3-way over the top gives roots somewhere to actually go. Break up or rototill the surface underneath before you spread it so you do not create a hard boundary that roots stop at and water sits on.`,
    depths: [
      { job: 'New lawn from seed or sod', depth: '4" spread and raked level' },
      { job: 'Topdressing an existing lawn', depth: '1/2–1"' },
      { job: 'Garden and flower beds', depth: '8–12"' },
      { job: 'Raised bed fill', depth: 'Full bed depth, mounded slightly' },
      { job: 'Levelling low spots', depth: 'Fill to grade, allow for settling' },
    ],
    caution: {
      title: 'Allow for settling',
      body: 'Blended soil settles roughly 10–20% once it is watered in, so order a little over your calculated volume and mound slightly when you place it. And keep it out of structural work — soil under a patio, pad, or driveway will always fail. That job wants 5/8" Minus.',
    },
    faqs: (city, price) => [
      {
        q: `How deep should topsoil be for a new lawn?`,
        a: `Four inches of 3-way topsoil is the standard for a new lawn from seed or sod. Loosen whatever is underneath first so roots can pass through, rake the soil level, then seed. For topdressing an established lawn, a half inch to an inch is plenty.`,
      },
      {
        q: `What is the difference between 3-way topsoil and compost?`,
        a: `3-way is a blend of screened topsoil, sand, and compost — a complete growing medium you can plant straight into. Straight compost is an amendment: too rich and too light on its own for a lawn, but excellent worked into existing beds.`,
      },
      {
        q: `Can I use topsoil to level a driveway or patio base?`,
        a: `No. Soil holds water, settles, and never compacts into a stable layer, so anything built on it moves. Use 5/8" Minus for patio bases and driveways, and keep topsoil for lawns and planting beds.`,
      },
      {
        q: `How much topsoil do I need in ${city}?`,
        a: `One yard covers about 80 sq ft at 4 inches deep. Enter your dimensions in the calculator on this page for an exact number, then add roughly 10% for settling. Material is ${price} per yard plus the ${city} delivery rate.`,
      },
    ],
  },
  'medium-bark': {
    what: 'Shredded fir and hemlock bark at a medium grind — what most people around here mean when they say mulch. Spread over beds it shades out weed seed, holds moisture through a dry August, insulates roots against a cold snap, and gives a yard that finished, uniform look. The medium grind is the useful middle: fine enough to spread and rake cleanly, coarse enough to knit together and stay put on a slope instead of floating off in heavy rain. It breaks down slowly over a couple of seasons, feeding the soil as it goes, which is why most beds want a refresh rather than a full replacement each spring.',
    uses: [
      'Flower and shrub beds',
      'Tree rings',
      'Slopes and banks',
      'Weed suppression',
      'Moisture retention through summer',
      'Informal garden pathways',
    ],
    localAngle: (city) =>
      `Two things make bark work hard in ${city}: the wet half of the year, when a medium grind knits together and stays on a slope where a fine bark would wash out, and the dry stretch from July through September, when 2–3 inches over a bed cuts watering noticeably. Refresh in spring before weeds germinate and you get the whole season out of one load.`,
    depths: [
      { job: 'New bed installation', depth: '3" over weeded soil' },
      { job: 'Annual refresh', depth: '1–2" over existing bark' },
      { job: 'Tree rings', depth: '3", pulled back from the trunk' },
      { job: 'Slopes and banks', depth: '3" — medium grind holds best' },
      { job: 'Informal pathways', depth: '3–4"' },
    ],
    caution: {
      title: 'Keep it off trunks and stems',
      body: 'Bark piled against a trunk traps moisture and invites rot and pests. Leave a few inches of clear space around trees and shrubs. Bark is also a surface mulch, not a soil amendment — do not till it in, because breaking it down pulls nitrogen away from your plants.',
    },
    faqs: (city, price) => [
      {
        q: `How deep should bark mulch be?`,
        a: `Three inches on a new bed, and one to two inches to refresh a bed that still has bark on it. Less than two inches will not suppress weeds; much more than four can hold too much moisture against crowns and stems.`,
      },
      {
        q: `Will bark wash off a slope?`,
        a: `A medium grind is the right choice for slopes — the shredded pieces knit together and hold far better than a fine bark, which floats. On a steep bank, spread 3 inches and water it in so it settles and locks.`,
      },
      {
        q: `How often should I re-bark my beds?`,
        a: `Most ${city} yards refresh once a year in spring, before weed seed germinates. Bark breaks down slowly, so it is usually a one-to-two-inch top-up rather than a full replacement.`,
      },
      {
        q: `How much bark do I need?`,
        a: `One yard covers about 100 sq ft at 3 inches deep. Measure your beds and run them through the calculator on this page for a precise figure. Bark is ${price} per yard plus the ${city} haul, and it is light, so a full load goes a long way.`,
      },
    ],
  },
}
// Slug used by the shared calculator's density table, which predates the
// catalog slugs and doesn't match them one-to-one.
export const CALC_MATERIAL_IDS: Record<string, string> = {
  '5-8-minus': 'crushed-rock',
  '1-25-minus': '125-minus',
  '5-8-clear': '58-clear',
  '7-8-drain-rock': '78-drain',
  '3-way-topsoil': 'topsoil',
  'medium-bark': 'medium-bark',
}
export function getServiceProduct(slug: string) {
  for (const cat of categories) {
    const p = cat.products.find((pp) => pp.slug === slug)
    if (p)
      return {
        product: p,
        category: cat,
      }
  }
  return null
}
export function getServiceCity(slug: string): ServiceCity | undefined {
  return SERVICE_CITIES.find((c) => c.slug === slug)
}

/**
 * Nearby service cities, nearest first, by actual straight-line distance.
 *
 * This used to filter on the `county` field and take the first six in array
 * order, which produced links a local would never make — Seattle suggesting
 * Medina and Black Diamond ahead of Burien and Tukwila, because those happen
 * to sit earlier in the array and share a county. County lines also cut
 * genuinely adjacent pairs apart (Sumner/Auburn, Tacoma/Federal Way).
 *
 * Distance uses the city centroids already stored on each record for the
 * zone-drift check, so no new data is needed.
 */
export function getNearbyCities(city: ServiceCity, limit = 6): ServiceCity[] {
  // Equirectangular approximation — over the ~40 miles this list spans it is
  // accurate to well within the ordering precision we need, and it avoids
  // trig per comparison.
  const MILES_PER_DEG_LAT = 69.0
  const milesPerDegLng =
    MILES_PER_DEG_LAT * Math.cos((city.coords[0] * Math.PI) / 180)
  const distance = (other: ServiceCity) => {
    const dLat = (other.coords[0] - city.coords[0]) * MILES_PER_DEG_LAT
    const dLng = (other.coords[1] - city.coords[1]) * milesPerDegLng
    return Math.sqrt(dLat * dLat + dLng * dLng)
  }
  return SERVICE_CITIES.filter((c) => c.slug !== city.slug)
    .map((c) => ({ city: c, d: distance(c) }))
    .sort((a, b) => a.d - b.d)
    .slice(0, limit)
    .map((entry) => entry.city)
}

// ---------- Dev-only zone-consistency check ----------
//
// Each ServiceCity carries a curated `zone` value used by the city
// landing pages and SEO copy. The cart's delivery-fee math, however,
// uses the live polygons in `deliveryZones.ts` via getZoneForPosition.
// If the polygons get redrawn and someone forgets to update the curated
// list (or vice versa), the city page and the cart can disagree about
// the fee — bad UX and a trust hit.
//
// This check runs once at module-load in development and warns in the
// browser console for any city whose curated `zone` doesn't match what
// the polygons say. It's a no-op in production builds, and it never
// auto-corrects — the operator decides which source is authoritative
// and updates the other to match.
if (typeof process !== 'undefined' && process.env.NODE_ENV !== 'production') {
  const mismatches: { slug: string; curated: number; polygon: number }[] = []
  for (const city of SERVICE_CITIES) {
    const result = getZoneForPosition(city.coords[0], city.coords[1])
    if (result.zone !== city.zone) {
      mismatches.push({
        slug: city.slug,
        curated: city.zone,
        polygon: result.zone,
      })
    }
  }
  if (mismatches.length > 0 && typeof console !== 'undefined') {
    // eslint-disable-next-line no-console
    console.warn(
      '[SERVICE_CITIES] Zone drift detected — city pages and the cart will quote different delivery fees:\n' +
        mismatches
          .map(
            (m) =>
              `  • ${m.slug}: curated zone ${m.curated} vs polygon zone ${m.polygon || 'outside'}`,
          )
          .join('\n') +
        '\nUpdate either data/cityMaterialContent.ts (city zone) or data/deliveryZones.ts (polygon) so they agree.',
    )
  }
}
